import {
  Box,
  Button,
  Drawer,
  Slider,
  Stack,
  Typography,
  styled,
} from "@mui/material";
import { useUnit } from "effector-react";
import { useEffect, useRef, useState } from "react";

import images from "../constants/images";
import { STEP_TUTORIAL, type StepTutorialTypes } from "../constants/tutorial";
import { $gameConfig, $playerData, exchangeResources } from "../store";
import type {
  ResourceObjects,
  TokenValleyBlockType,
} from "../types/tokenValley";
import { formatTypesName } from "../utils/formatTypesName";
import { getResourceGatheringIconResourcesTypes } from "../utils/getResourceGatheringIcon";
import HandTutorial from "./ui/HandTutorial";

interface TypesProps {
  open: boolean;
  block: TokenValleyBlockType;
  stepTutorial: StepTutorialTypes;
  setOpen: (open: boolean) => void;
  setStepTutorial: (step: StepTutorialTypes) => void;
}

const DrawerEnterHouse = ({
  open,
  block,
  setOpen,
  stepTutorial,
  setStepTutorial,
}: TypesProps) => {
  const [percentage, setPercentage] = useState(100);
  const [gameConfig, playerData] = useUnit([$gameConfig, $playerData]);

  const [selectResource, setSelectResource] =
    useState<ResourceObjects>("resource/wheat");

  const maxAmount = selectResource
    ? playerData.player_resources?.[selectResource] ||
      Object.values(playerData.player_resources)[0]
    : 0;

  const [value, setValue] = useState<number>(maxAmount || 0);

  const lastSetValueRef = useRef<number | null>(null);

  const handleChange = (_: Event, newValue: number | number[]) => {
    const percent = newValue as number;
    setPercentage(percent);
    const calculatedValue = (percent / 100) * (maxAmount || 0);
    setValue(Math.floor(calculatedValue));
  };

  useEffect(() => {
    if (lastSetValueRef.current === null) {
      setValue(
        Math.floor(
          (percentage / 100) *
            (playerData.player_resources[selectResource] || 0),
        ),
      );
    }
  }, [selectResource, playerData.player_resources, percentage]);

  const price = gameConfig?.resources_price[selectResource];
  const sellPrice = value * (price || 0);

  const handleSell = () => {
    if (!selectResource) return;
    if (playerData.tutorial_step === 3) {
      setStepTutorial(STEP_TUTORIAL.SELL);
      setOpen(false);
    }

    exchangeResources({
      resource_amount: Math.floor(value),
      resource_price: price || 0,
      resource_type: selectResource,
      exchange_position: block.block_position,
    });

    const newMaxAmount = Math.max(0, (maxAmount || 0) - value);
    setValue(0);

    setValue(newMaxAmount);
  };

  return (
    <Drawer
      open={open}
      anchor="bottom"
      onClose={() => {
        if (stepTutorial === STEP_TUTORIAL.OPEN_SELL) {
          return;
        }
        setOpen(false);
        setSelectResource("resource/wheat");
      }}
      PaperProps={{
        sx: {
          borderTop: "3px solid",
          pb: 2,
          background: "linear-gradient( to top, #89a8dc, #7FA0DC)",
          borderColor: "primary.borderSideBar",
        },
      }}
    >
      <Box height="100%" role="presentation" padding={"15px 20px"}>
        <Typography
          color="white"
          sx={{
            textShadow:
              "-2px -2px 2px #344871, 2px -2px 2px #344871, -2px 2px 2px #344871, 2px 2px 2px #344871",
          }}
        >
          Extracted resources
        </Typography>
        <Typography my={1} fontSize={"12px"} color="white">
          Select a resource
        </Typography>

        {Object.keys(playerData.player_resources).length === 0 ? (
          <Typography color="white" textAlign="center">
            Empty
          </Typography>
        ) : (
          Object.entries(playerData.player_resources).map(
            ([resource_type, amount]) => {
              if (resource_type !== "resource/token") {
                return (
                  <Stack
                    key={resource_type}
                    p={1}
                    mb={1}
                    direction="row"
                    borderRadius="8px"
                    border="2px solid"
                    borderColor={
                      selectResource === resource_type
                        ? "primary.blue"
                        : "transparent"
                    }
                    justifyContent="space-between"
                    bgcolor="primary.borderSideBar"
                    onClick={() => {
                      setSelectResource(resource_type as ResourceObjects);
                    }}
                  >
                    <Stack direction="row" alignItems="center" gap={1}>
                      <img
                        src={
                          getResourceGatheringIconResourcesTypes(
                            resource_type as ResourceObjects,
                          ) || undefined
                        }
                        style={{ width: "35px" }}
                        alt={`${resource_type} icon`}
                      />
                      <Typography color="white">
                        {formatTypesName(resource_type)}
                      </Typography>
                    </Stack>
                    <Typography color="white" alignContent={"center"}>
                      {amount?.toFixed(0)}
                    </Typography>
                  </Stack>
                );
              }
            },
          )
        )}
      </Box>

      {selectResource && (
        <Stack px={3}>
          <Stack direction={"row"} justifyContent={"space-between"}>
            <Typography fontSize={"12px"} noWrap color="white">
              {value} / {maxAmount?.toFixed(0)}
            </Typography>
            <Stack direction={"row"} gap={1}>
              <Typography fontSize={"12px"} noWrap color="white">
                {sellPrice.toFixed(5)}
              </Typography>
              <img
                src={images.icons.TCoin}
                alt="TCoin"
                style={{ width: "20px" }}
              />
            </Stack>
          </Stack>
          <Stack mt={1} mb={2} direction="row" alignItems="center" gap={3}>
            <PrettoSlider
              min={0}
              step={1}
              max={100}
              aria-label="sell"
              value={percentage}
              onChange={handleChange}
              valueLabelDisplay="auto"
              valueLabelFormat={(value) => `${value}%`}
            />
            <Button
              disabled={value === 0}
              variant="blue"
              onClick={handleSell}
              sx={{ height: "30px", width: "100px" }}
            >
              Sell
            </Button>
            {playerData.tutorial_step === 3 &&
              stepTutorial === STEP_TUTORIAL.OPEN_SELL && (
                <HandTutorial bottom="10px" right="35px" animation="click" />
              )}
          </Stack>
        </Stack>
      )}
    </Drawer>
  );
};
const PrettoSlider = styled(Slider)({
  color: "white",
  "& .MuiSlider-valueLabel": {
    width: 32,
    height: 32,
    fontSize: 12,
    lineHeight: 1.2,
    backgroundColor: "#3BB1F3",
    borderRadius: "50% 50% 50% 0",
    "& > *": {
      transform: "rotate(45deg)",
    },
    transformOrigin: "bottom left",
    "&::before": { display: "none" },
    transform: "translate(50%, -100%) rotate(-45deg) scale(0)",
    "&.MuiSlider-valueLabelOpen": {
      transform: "translate(50%, -100%) rotate(-45deg) scale(1)",
    },
  },
});
export default DrawerEnterHouse;
